import React, { useState, useEffect } from "react";
import { IEvent, IShow, IScentworkAwards } from "@eagerdog/interfaces";
import { apiService } from "../../../services/api.service";
import { toast } from "../../Toast/ToastManager";
import { calculateScentworkAwards } from "@eagerdog/awards-util";
import { Constants } from "@eagerdog/constants";

import "./AkcScentworkAwards.scss";

interface IProps {
  event: IEvent;
  show: IShow;
  scores?: any[];
}

const AkcScentworkAwards: React.FC<IProps> = ({ event, show, scores: providedScores }) => {
  const [loading, setLoading] = useState(!providedScores);
  const [awards, setAwards] = useState<IScentworkAwards | null>(null);

  useEffect(() => {
    loadScoresAndCalculateAwards();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [event._id, show._id, providedScores]);

  const loadScoresAndCalculateAwards = async () => {
    try {
      setLoading(true);
      const scores = providedScores || await apiService.getEventScores(event._id.toString());

      // Filter scores for this show
      const showScores = scores.filter(
        (s) =>
          s.show_type === show.show_type &&
          s.show_id.toString() === show._id.toString()
      );

      const calculatedAwards = calculateScentworkAwards(showScores);
      setAwards(calculatedAwards);
    } catch (error) {
      toast.show({
        title: "Error",
        content: "Failed to load scores for awards calculation",
        duration: 5000,
        type: "fail",
      });
    } finally {
      setLoading(false);
    }
  };

  const formatTime = (seconds: number): string => {
    const minutes = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    const milliseconds = Math.round((seconds % 1) * 100);
    return `${String(minutes).padStart(2, '0')}:${String(secs).padStart(2, '0')}:${String(milliseconds).padStart(2, '0')}`;
  };

  const formatLevel = (level: string): string => {
    const levelMap: { [key: string]: string } = {
      [Constants.dog_class_element_level.novice]: "Novice (A & B)",
      [Constants.dog_class_element_level.novice_a]: "Novice A",
      [Constants.dog_class_element_level.novice_b]: "Novice B",
      [Constants.dog_class_element_level.advanced]: "Advanced",
      [Constants.dog_class_element_level.excellent]: "Excellent",
      [Constants.dog_class_element_level.master]: "Master",
    };
    return levelMap[level] || level;
  };

  const formatElement = (element: string): string => {
    const elementMap: { [key: string]: string } = {
      [Constants.dog_class_element.scentwork_container]: "Container",
      [Constants.dog_class_element.scentwork_interior]: "Interior",
      [Constants.dog_class_element.scentwork_exterior]: "Exterior",
      [Constants.dog_class_element.scentwork_buried]: "Buried",
      [Constants.dog_class_element.scentwork_detective]: "Detective",
    };
    return elementMap[element] || element;
  };

  if (loading) {
    return <div className="AkcScentworkAwards loading">Loading awards...</div>;
  }

  if (!awards) {
    return <div className="AkcScentworkAwards error">Failed to calculate awards</div>;
  }

  return (
    <div className="AkcScentworkAwards">
      {/* High in Trial Section */}
      <div className="hitSection">
        <h3>High in Trial</h3>
        {awards.hitElements && awards.hitElements.length > 0 && (
          <p className="awardDetail">
            Based on the elements offered in this trial: {awards.hitElements.join(", ")}. A dog must qualify in each of them; fastest combined time wins.
          </p>
        )}
        {awards.highInTrial.length === 0 ? (
          <p className="noWinner">
            {awards.hitElements && awards.hitElements.length > 0
              ? `No dog qualified in every offered element (${awards.hitElements.join(", ")}) at the same level`
              : "No qualifying scores for High in Trial"}
          </p>
        ) : (
          awards.highInTrial.map((hit, index) => (
            <div key={index} className="awardSection">
              <h4>{formatLevel(hit.level)}</h4>
              <p className="awardWinner">
                #{hit.arm_number} {hit.call_name} ({hit.breed}) – Combined Time: {formatTime(hit.combined_time)}
              </p>
              <p className="awardDetail">
                {hit.elements.map((e) => `${e.element}: ${formatTime(e.time)}`).join(", ")}
              </p>
            </div>
          ))
        )}
      </div>

      {/* First Place Section */}
      <div className="firstPlaceSection">
        <h3>First Place Winners</h3>
        {awards.firstPlace.length === 0 ? (
          <p className="noWinner">No qualifying scores for first place</p>
        ) : (
          <>
            {[
              Constants.dog_class_element.scentwork_container,
              Constants.dog_class_element.scentwork_interior,
              Constants.dog_class_element.scentwork_exterior,
              Constants.dog_class_element.scentwork_buried,
              Constants.dog_class_element.scentwork_detective,
            ].map((element) => {
              const elementWinners = awards.firstPlace.filter(fp => fp.element === element);
              if (elementWinners.length === 0) return null;

              return (
                <div key={element} className="elementGroup">
                  <h4>{formatElement(element)}</h4>
                  {elementWinners.map((winner, index) => (
                    <div key={index} className="awardSection">
                      <p className="levelTitle">{formatLevel(winner.level)}</p>
                      <p className="awardWinner">
                        #{winner.arm_number} {winner.call_name} ({winner.breed}) – Time: {formatTime(winner.time)}
                      </p>
                    </div>
                  ))}
                </div>
              );
            })}
          </>
        )}
      </div>
    </div>
  );
};

export default AkcScentworkAwards;

